import { createAction, createSelector, props } from '@ngrx/store';
import {
  MockupScene,
  MockupSceneCategory,
  MockupSceneCreateRequest,
  MockupSceneListFilter,
  MockupSceneTag,
  MockupSceneUpdateRequest,
  ProductCategoryUid,
} from '@api-ui-app/src/app/mockup-studio/types/mockup-studio-scenes';
import { getSanityProducts } from '@gelato-api-ui/sdk/src/lib/sanity/sanity.selector';
import { SanityProduct, SanityProductEnum } from '@gelato-api-ui/core/sanity/sanity-product';
import { MockupAsset, MockupAssetListRequest } from '@api-ui-app/src/app/mockup-studio/types/mockup-studio-assets';
import { SanityProductVariantFrameAssembling } from '@gelato-api-ui/core/sanity/sanity-product-variant';

export const loadMockupList = createAction(
  '[Mockup Studio] Load Mockup List',
  props<{ filter: MockupSceneListFilter }>(),
);
export const loadMockupListStart = createAction('[Mockup Studio] Load Mockup List Start');
export const loadMockupListSuccess = createAction(
  '[Mockup Studio] Load Mockup List Success',
  props<{ mockupScenes: MockupScene[] }>(),
);
export const loadMockupListFailed = createAction('[Mockup Studio] Load Mockup List Failed');

export const loadPublicMockupList = createAction(
  '[Mockup Studio] Load Public Mockup List',
  props<{ filter: MockupSceneListFilter }>(),
);
export const loadPublicMockupListStart = createAction('[Mockup Studio] Load Public Mockup List Start');
export const loadPublicMockupListSuccess = createAction(
  '[Mockup Studio] Load Public Mockup List Success',
  props<{ mockupScenes: MockupScene[] }>(),
);
export const loadPublicMockupListFailed = createAction('[Mockup Studio] Public Load Mockup List Failed');

export const loadPublicCategorizedScenes = createAction(
  '[Mockup Studio] Load Public Categorized Scenes',
  props<{ productCategoryUid: string }>(),
);
export const loadPublicCategorizedScenesStart = createAction('[Mockup Studio] Load Public Categorized Scenes Start');
export const loadPublicCategorizedScenesSuccess = createAction(
  '[Mockup Studio] Load Public Categorized Scenes Success',
  props<{ categories: MockupSceneCategory[] }>(),
);
export const loadPublicCategorizedScenesFailed = createAction('[Mockup Studio] Load Public Categorized Scenes Failed');

export const loadMockupSceneTags = createAction('[Mockup Studio] Load Mockup Scene Tags');
export const loadMockupSceneTagsStart = createAction('[Mockup Studio] Load Mockup Scene Tags Start');
export const loadMockupSceneTagsSuccess = createAction(
  '[Mockup Studio] Load Mockup Scene Tags Success',
  props<{ tags: MockupSceneTag[] }>(),
);
export const loadMockupSceneTagsFailed = createAction('[Mockup Studio] Load Mockup Scene Tags Failed');

export const loadAssetList = createAction(
  '[Mockup Studio] Load Asset List',
  props<{ request: MockupAssetListRequest; force?: boolean }>(),
);
export const loadAssetListStart = createAction('[Mockup Studio] Load Asset List Start');
export const loadAssetListSuccess = createAction(
  '[Mockup Studio] Load Asset List Success',
  props<{ mockupAssets: MockupAsset[] }>(),
);
export const loadAssetListFailed = createAction('[Mockup Studio] Load Asset List Failed');

export const createScene = createAction('[Mockup Studio] Create Scene', props<{ request: MockupSceneCreateRequest }>());
export const createSceneSuccess = createAction('[Mockup Studio] Create Scene Success', props<{ scene: MockupScene }>());
export const createSceneFailed = createAction('[Mockup Studio] Create Scene Failed');

export const updateScene = createAction('[Mockup Studio] Update Scene', props<{ request: MockupSceneUpdateRequest }>());
export const updateSceneSuccess = createAction('[Mockup Studio] Update Scene Success', props<{ scene: MockupScene }>());
export const updateSceneFailed = createAction('[Mockup Studio] Update Scene Failed');

export const duplicateScene = createAction('[Mockup Studio] Duplicate Scene', props<{ id: string; name: string }>());
export const duplicateSceneSuccess = createAction(
  '[Mockup Studio] Duplicate Scene Success',
  props<{ scene: MockupScene }>(),
);
export const duplicateSceneFailed = createAction('[Mockup Studio] Duplicate Scene Failed');

export const deleteScene = createAction('[Mockup Studio] Delete Scene', props<{ id: string }>());
export const deleteSceneSuccess = createAction('[Mockup Studio] Delete Scene Success', props<{ id: string }>());
export const deleteSceneFailed = createAction('[Mockup Studio] Delete Scene Failed', props<{ id: string }>());

export const getSanityProductsForEditor = createSelector(getSanityProducts, products => {
  const returnedProduct: SanityProduct[] = [];
  if (!products) {
    return returnedProduct;
  }

  Object.keys(products).forEach(id => {
    const product = products[id].product;

    if (id === SanityProductEnum.standardFramedPosters) {
      returnedProduct.push({
        ...product,
        // we do not care about mounted/unmounted frames in the editor
        productVariations: product.productVariations.filter(
          variation => variation.frameassembling === SanityProductVariantFrameAssembling.notMounted,
        ),
      });
    } else {
      returnedProduct.push(product);
    }
  });

  return returnedProduct;
});

export const mockupStudioCreateAsset = createAction(
  '[Mockup Studio] Create Asset',
  props<{ fileUrl: string; id: string }>(),
);
export const mockupStudioCreateAssetSuccess = createAction(
  '[Mockup Studio] Create Asset Success',
  props<{ asset: MockupAsset; id: string }>(),
);
export const mockupStudioCreateAssetFailed = createAction('[Mockup Studio] Create Asset Failed');

export const mockupStudioDeleteAsset = createAction('[Mockup Studio] Delete Asset', props<{ id: string }>());
export const mockupStudioDeleteAssetSuccess = createAction(
  '[Mockup Studio] Delete Asset Success',
  props<{ id: string }>(),
);
export const mockupStudioDeleteAssetFailed = createAction('[Mockup Studio] Delete Asset Failed');

export const loadProductCategoryUids = createAction('[Mockup Studio] Load Product Category Uids');
export const loadProductCategoryUidsStart = createAction('[Mockup Studio] Load Product Category Uids Start');
export const loadProductCategoryUidsSuccess = createAction(
  '[Mockup Studio] Load Product Category Uids Success',
  props<{ categories: ProductCategoryUid[] }>(),
);
export const loadProductCategoryUidsFailed = createAction('[Mockup Studio] Load Product Category Uids Failed');
